import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Mail, ArrowUp, ChevronRight, Lock } from 'lucide-react';
import { useSiteBrand } from '../components/BrandLogo';

const LAST_UPDATE = '1 de marzo de 2026';

const SECTIONS = [
  { id: 'responsable',  num: '01', title: 'Responsable del tratamiento' },
  { id: 'datos',        num: '02', title: 'Datos que recopilamos' },
  { id: 'finalidad',    num: '03', title: 'Finalidad del tratamiento' },
  { id: 'terceros',     num: '04', title: 'Transferencia a terceros' },
  { id: 'arco',         num: '05', title: 'Derechos ARCO' },
  { id: 'cookies',      num: '06', title: 'Cookies y tecnologías similares' },
  { id: 'cambios',      num: '07', title: 'Cambios al aviso' },
  { id: 'contacto',     num: '★',  title: 'Contacto' },
];

export default function PrivacidadPage() {
  const brand = useSiteBrand();
  const siteName   = brand?.site_name    || 'NubesMTY';
  const email      = brand?.contact_email || brand?.support_email || 'soporte@nubesmty.com';
  const [active, setActive]   = useState('responsable');
  const [progress, setProgress] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (el) {
        const total   = el.scrollHeight - window.innerHeight;
        const scrolled = Math.max(0, Math.min(window.scrollY - el.offsetTop, total));
        setProgress(total > 0 ? (scrolled / total) * 100 : 0);
      }
      let current = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const node = document.getElementById(s.id);
        if (node && node.getBoundingClientRect().top - 140 <= 0) current = s.id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 110, behavior: 'smooth' });
  };

  return (
    <div ref={containerRef} className="min-h-[calc(100vh-160px)] pb-24">
      {/* Reading progress */}
      <div className="fixed top-0 left-0 right-0 h-[3px] z-[60] bg-white/[0.03]">
        <div
          className="h-full bg-gradient-to-r from-orange-500 via-orange-400 to-red-500 transition-[width] duration-150"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 w-[420px] h-[420px] rounded-full bg-orange-500/15 blur-[100px]" />
          <div className="absolute bottom-0 right-1/3 w-[380px] h-[380px] rounded-full bg-red-600/10 blur-[100px]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="flex items-center gap-2 text-orange-400 text-xs font-bold uppercase tracking-widest mb-4">
            <Lock className="w-3.5 h-3.5" />
            <span>Privacidad</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.05] mb-4">
            Aviso de<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">
              Privacidad
            </span>
          </h1>
          <p className="text-white/60 text-base max-w-xl">
            En {siteName} protegemos tu información personal con total transparencia.
            Este aviso explica qué datos recopilamos, para qué los usamos y cuáles son tus derechos.
          </p>
          <p className="mt-4 text-xs text-white/40">Última actualización: {LAST_UPDATE}</p>
        </div>
      </section>

      {/* Body: sidebar + content */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-10 flex gap-10">

        {/* Sidebar TOC */}
        <aside className="hidden lg:block w-60 shrink-0 sticky top-28 self-start">
          <p className="text-[10px] uppercase tracking-widest text-white/30 font-bold mb-4">Contenido</p>
          <nav className="space-y-1">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors
                  ${active === s.id
                    ? 'bg-orange-500/15 text-orange-400 font-semibold'
                    : 'text-white/50 hover:text-white hover:bg-white/5'}`}
              >
                <span className={`text-[10px] font-black w-5 ${active === s.id ? 'text-orange-500' : 'text-white/25'}`}>
                  {s.num}
                </span>
                {s.title}
              </button>
            ))}
          </nav>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="mt-6 flex items-center gap-1.5 text-xs text-white/30 hover:text-orange-400 transition-colors"
          >
            <ArrowUp className="w-3.5 h-3.5" /> Volver arriba
          </button>
        </aside>

        {/* Main content */}
        <article className="flex-1 min-w-0 space-y-12 text-white/80 text-[15px] leading-relaxed">

          {/* 01 */}
          <section id="responsable">
            <SectionHeader num="01" title="Responsable del tratamiento" />
            <p>
              El responsable del tratamiento de tus datos personales es <strong className="text-white">NubesMTY</strong>,
              tienda en línea con dominio <strong className="text-white">nubesmty.com</strong>, con sede en
              Monterrey, Nuevo León, México.
            </p>
            <p className="mt-3">
              Para cualquier consulta relacionada con la privacidad de tus datos puedes contactarnos en:&nbsp;
              <a href={`mailto:${email}`} className="text-orange-400 hover:underline">{email}</a>.
            </p>
          </section>

          {/* 02 */}
          <section id="datos">
            <SectionHeader num="02" title="Datos que recopilamos" />
            <p>Para brindarte nuestros servicios recopilamos los siguientes datos personales:</p>
            <ul className="mt-4 space-y-2.5">
              {[
                ['Nombre completo', 'Para identificarte como cliente y elaborar tu pedido.'],
                ['Dirección de entrega', 'Calle, número, colonia, código postal, ciudad y estado requeridos para el envío.'],
                ['Número de teléfono', 'Para coordinar la entrega y resolver cualquier incidencia con tu pedido.'],
                ['Correo electrónico', 'Para enviarte confirmaciones de pedido, actualizaciones de envío y responder tus consultas.'],
              ].map(([campo, desc]) => (
                <li key={campo} className="flex gap-3">
                  <ChevronRight className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
                  <span><strong className="text-white">{campo}:</strong> {desc}</span>
                </li>
              ))}
            </ul>
            <InfoBox className="mt-5">
              No recopilamos datos sensibles como información financiera completa, datos de salud ni
              números de tarjeta de crédito/débito. Los pagos por transferencia se realizan directamente
              mediante tu banco, sin intermediación de {siteName}.
            </InfoBox>
          </section>

          {/* 03 */}
          <section id="finalidad">
            <SectionHeader num="03" title="Finalidad del tratamiento" />
            <p>Tus datos personales son utilizados exclusivamente para las siguientes finalidades <strong className="text-white">necesarias</strong>:</p>
            <ul className="mt-4 space-y-2.5">
              {[
                'Procesar y gestionar tus pedidos en línea.',
                'Coordinar y realizar el envío de los productos a tu domicilio.',
                'Enviarte confirmaciones, actualizaciones de estado y comprobantes de tu pedido.',
                'Atender dudas, aclaraciones o reclamaciones relacionadas con tus compras.',
                'Cumplir con las obligaciones legales y fiscales que correspondan.',
              ].map((item) => (
                <li key={item} className="flex gap-3">
                  <ChevronRight className="w-4 h-4 mt-0.5 text-orange-500 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* 04 */}
          <section id="terceros">
            <SectionHeader num="04" title="Transferencia a terceros" />
            <p>
              <strong className="text-white">No compartimos, vendemos ni cedemos</strong> tus datos personales
              a terceros con fines comerciales o publicitarios.
            </p>
            <p className="mt-3">
              La única excepción es la necesaria para completar tu pedido: compartimos tu nombre,
              dirección y teléfono con la <strong className="text-white">empresa de paquetería o mensajería</strong> (por
              ejemplo DHL o Pikpak) únicamente en la medida en que sea indispensable para realizar la entrega.
              Dichos prestadores de servicios están obligados a tratar tu información de forma confidencial
              y sólo para ese fin.
            </p>
            <InfoBox className="mt-5">
              Fuera del supuesto anterior, tus datos no son transferidos a ningún tercero, salvo obligación
              legal expresa de las autoridades competentes.
            </InfoBox>
          </section>

          {/* 05 */}
          <section id="arco">
            <SectionHeader num="05" title="Derechos ARCO" />
            <p>
              De conformidad con la <strong className="text-white">Ley Federal de Protección de Datos Personales
              en Posesión de los Particulares (LFPDPPP)</strong>, tienes derecho a:
            </p>
            <div className="mt-5 grid sm:grid-cols-2 gap-4">
              {[
                { letra: 'A', titulo: 'Acceso', desc: 'Conocer qué datos personales tuyos tenemos, para qué los usamos y las condiciones del uso.' },
                { letra: 'R', titulo: 'Rectificación', desc: 'Corregir tus datos cuando sean inexactos, incompletos o estén desactualizados.' },
                { letra: 'C', titulo: 'Cancelación', desc: 'Solicitar la supresión de tus datos cuando consideres que no son necesarios para la finalidad declarada.' },
                { letra: 'O', titulo: 'Oposición', desc: 'Oponerte al tratamiento de tus datos para determinadas finalidades que te causen un daño o perjuicio.' },
              ].map(({ letra, titulo, desc }) => (
                <div key={letra} className="rounded-xl border border-white/10 bg-white/[0.03] p-4 flex gap-3">
                  <span className="w-9 h-9 rounded-lg bg-orange-500/15 text-orange-400 font-black text-lg flex items-center justify-center shrink-0">
                    {letra}
                  </span>
                  <div>
                    <p className="font-bold text-white text-sm">{titulo}</p>
                    <p className="text-xs text-white/60 mt-0.5 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5">
              Para ejercer cualquiera de estos derechos, envíanos un correo a&nbsp;
              <a href={`mailto:${email}`} className="text-orange-400 hover:underline">{email}</a>&nbsp;
              indicando tu nombre, el derecho que deseas ejercer y los datos relevantes. Responderemos
              en un plazo máximo de <strong className="text-white">20 días hábiles</strong>.
            </p>
          </section>

          {/* 06 */}
          <section id="cookies">
            <SectionHeader num="06" title="Cookies y tecnologías similares" />
            <p>
              Nuestro sitio utiliza <strong className="text-white">almacenamiento local (localStorage)</strong> para
              mantener tu sesión activa, guardar el carrito de compras y recordar tus preferencias de
              navegación. No utilizamos cookies de rastreo de terceros con fines publicitarios.
            </p>
            <p className="mt-3">
              Puedes limpiar estos datos en cualquier momento desde la configuración de tu navegador
              o dispositivo, aunque esto puede afectar la funcionalidad de la tienda.
            </p>
          </section>

          {/* 07 */}
          <section id="cambios">
            <SectionHeader num="07" title="Cambios al aviso" />
            <p>
              Nos reservamos el derecho de actualizar este Aviso de Privacidad en cualquier momento.
              Cualquier modificación será publicada en esta misma página con una nueva fecha de
              actualización. Te recomendamos consultarla periódicamente.
            </p>
            <p className="mt-3">
              El uso continuo de nuestros servicios tras la publicación de cambios implica tu aceptación
              de las condiciones actualizadas.
            </p>
          </section>

          {/* Contacto */}
          <section id="contacto" className="rounded-2xl border border-orange-500/20 bg-orange-500/[0.04] p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-500/15 text-orange-400 flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-orange-500/70 font-bold">★ Contacto</p>
                <h3 className="text-white font-bold">¿Tienes dudas sobre tu privacidad?</h3>
              </div>
            </div>
            <p className="text-white/60 text-sm mb-5">
              Si tienes preguntas, solicitudes ARCO o cualquier inquietud sobre el tratamiento de tus
              datos personales, no dudes en contactarnos. Estamos para ayudarte.
            </p>
            <a
              href={`mailto:${email}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 hover:bg-orange-400 text-black font-bold text-sm transition-colors"
              data-testid="privacidad-contact-email"
            >
              <Mail className="w-4 h-4" />
              {email}
            </a>
            <p className="mt-5 text-xs text-white/30">
              También puedes consultar la&nbsp;
              <Link to="/terminos-del-servicio" className="text-orange-400 hover:underline">
                página de Términos del Servicio
              </Link>.
            </p>
          </section>

        </article>
      </div>
    </div>
  );
}

function SectionHeader({ num, title }) {
  return (
    <div className="flex items-baseline gap-3 mb-4 pb-3 border-b border-white/8">
      <span className="text-[11px] font-black text-orange-500/60 tracking-widest">{num}</span>
      <h2 className="text-xl font-bold text-white">{title}</h2>
    </div>
  );
}

function InfoBox({ children, className = '' }) {
  return (
    <div className={`flex gap-3 rounded-xl border border-blue-500/20 bg-blue-500/[0.05] p-4 ${className}`}>
      <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" />
      <p className="text-sm text-white/70 leading-relaxed">{children}</p>
    </div>
  );
}
